import { Link } from "react-router-dom";
import { ArrowRight, Bell, Shield, BarChart3, Zap, Lock, Globe } from "lucide-react";

export default function Landing() {
  return (
    <>
      {/* Top Nav */}
      <header style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "var(--card-surface)",
        borderBottom: "1px solid var(--stone)"
      }}>
        <div className="container" style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 72
        }}>
          <Link to="/" style={{
            display: "flex", alignItems: "center", gap: 8,
            fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18,
            color: "var(--charcoal)", textDecoration: "none"
          }}>
            <Shield size={20} color="var(--accent)" />
            TokenGuard
          </Link>

          <nav style={{ display: "flex", alignItems: "center", gap: 28 }}>
            <a href="#features" style={{ fontSize: 14.5, fontWeight: 500, color: "var(--slate)", textDecoration: "none" }}>
              Features
            </a>
            <Link to="/pricing" style={{ fontSize: 14.5, fontWeight: 500, color: "var(--slate)", textDecoration: "none" }}>
              Pricing
            </Link>
            <Link to="/download" style={{ fontSize: 14.5, fontWeight: 500, color: "var(--slate)", textDecoration: "none" }}>
              Download
            </Link>
          </nav>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link to="/login" className="btn btn-outline" style={{ padding: "10px 20px", fontSize: 14.5 }}>
              Log in
            </Link>
            <Link to="/register" className="btn btn-primary" style={{ padding: "10px 20px", fontSize: 14.5 }}>
              Sign up
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="section" style={{ paddingTop: 100, paddingBottom: 80, background: "var(--cream)" }}>
        <div className="container">
          <div className="hero-grid" style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 60,
            alignItems: "center"
          }}>
            <div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 14px", borderRadius: 999,
                background: "rgba(46,125,107,0.1)", color: "var(--accent-dark)",
                fontSize: 12.5, fontWeight: 600, marginBottom: 24
              }}>
                <Zap size={14} /> Now available for Windows, macOS & Linux
              </div>
              <h1 style={{ fontSize: "clamp(36px, 5vw, 52px)", marginBottom: 20, color: "var(--charcoal)" }}>
                Stop overspending on<br />LLM tokens
              </h1>
              <p style={{ fontSize: 18, color: "var(--slate)", lineHeight: 1.6, marginBottom: 32, maxWidth: 480 }}>
                TokenGuard tracks usage across OpenAI, Gemini, Anthropic, Groq and more. 
                Set alerts, enforce cutoffs, and stay in control.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Link to="/download" className="btn btn-primary" style={{ padding: "16px 32px", fontSize: 16 }}>
                  Download for free <ArrowRight size={18} />
                </Link>
                <a href="#features" className="btn btn-outline" style={{ padding: "16px 32px", fontSize: 16 }}>
                  See how it works
                </a>
              </div>
            </div>
            <div style={{
              background: "var(--card-surface)",
              border: "1px solid var(--stone)",
              borderRadius: "var(--radius-lg)",
              padding: 32,
              boxShadow: "0 20px 60px rgba(0,0,0,0.08)"
            }}>
              <MockDashboard />
            </div>
          </div>
        </div>
      </section>

      {/* Logos / Social proof */}
      <section style={{ padding: "40px 0", borderBottom: "1px solid var(--stone)" }}>
        <div className="container" style={{ textAlign: "center" }}>
          <p style={{ fontSize: 12.5, color: "var(--muted-slate)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 20 }}>
            Works with your favorite providers
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 40, flexWrap: "wrap", opacity: 0.6 }}>
            <ProviderLogo text="OpenAI" />
            <ProviderLogo text="Gemini" />
            <ProviderLogo text="Anthropic" />
            <ProviderLogo text="Groq" />
            <ProviderLogo text="Mistral" />
            <ProviderLogo text="DeepSeek" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="section">
        <div className="container">
          <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto 60px" }}>
            <h2 style={{ fontSize: "clamp(28px, 3.5vw, 38px)", marginBottom: 14 }}>
              Everything you need to stay in budget
            </h2>
            <p style={{ fontSize: 17, color: "var(--slate)" }}>
              One dashboard. Every provider. Real-time control.
            </p>
          </div>

          <div className="feature-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 24
          }}>
            <FeatureCard
              icon={<BarChart3 size={22} color="var(--accent)" />}
              title="Real-time monitoring"
              desc="See token usage per provider, per URL, updated live. No more guessing where your budget went."
            />
            <FeatureCard
              icon={<Bell size={22} color="var(--accent)" />}
              title="Smart alerts"
              desc="Get notified when usage crosses a percentage or absolute threshold. Native OS notifications included."
            />
            <FeatureCard
              icon={<Shield size={22} color="var(--accent)" />}
              title="Auto cutoff"
              desc="Automatically block requests once a limit is hit. Reactivate with one click when you're ready."
            />
            <FeatureCard
              icon={<Lock size={22} color="var(--accent)" />}
              title="Secure by design"
              desc="Tokens stored in encrypted OS keychains. No credentials leak to the browser or cloud."
            />
            <FeatureCard
              icon={<Globe size={22} color="var(--accent)" />}
              title="Pass-through URLs"
              desc="Drop-in replacement base URLs for any provider. Zero code changes to your existing stack."
            />
            <FeatureCard
              icon={<Zap size={22} color="var(--accent)" />}
              title="Desktop native"
              desc="Built with Tauri for a tiny footprint. Auto-updates, system tray, and OS-native feel."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ background: "var(--accent)", color: "white", textAlign: "center" }}>
        <div className="container">
          <h2 style={{ fontSize: "clamp(26px, 3vw, 36px)", marginBottom: 16 }}>
            Ready to take control?
          </h2>
          <p style={{ fontSize: 17, opacity: 0.9, marginBottom: 32, maxWidth: 480, margin: "0 auto 32px" }}>
            Download TokenGuard free for Windows, macOS, or Linux. No credit card required.
          </p>
          <Link to="/download" className="btn" style={{
            background: "white", color: "var(--accent-dark)", padding: "16px 36px", fontSize: 16
          }}>
            Get TokenGuard <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}

function MockDashboard() {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--error)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f0c000" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--success)" }} />
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted-slate)", fontFamily: "var(--font-mono)" }}>TokenGuard Desktop</span>
      </div>

      <div style={{
        background: "var(--warm-white)",
        borderRadius: "var(--radius-md)",
        padding: 20,
        marginBottom: 16
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Plan usage (1M cycle)</span>
          <span style={{
            fontSize: 11, fontWeight: 700,
            padding: "2px 8px", borderRadius: 6,
            background: "rgba(46,125,107,0.12)", color: "var(--accent-dark)"
          }}>62.4%</span>
        </div>
        <div style={{ width: "100%", height: 10, borderRadius: 8, background: "var(--bar-track)", overflow: "hidden" }}>
          <div style={{ width: "62.4%", height: "100%", borderRadius: 8, background: "var(--bar-fill)" }} />
        </div>
        <p style={{ fontSize: 11.5, color: "var(--slate)", marginTop: 8 }}>624,012 lifetime used / 1,000,000 available</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <MockRow name="OpenAI" pct={58} />
        <MockRow name="Gemini" pct={42} />
        <MockRow name="Anthropic" pct={12} />
      </div>
    </div>
  );
}

function MockRow({ name, pct }) {
  return (
    <div style={{
      background: "var(--elevated-surface)",
      borderRadius: "var(--radius-sm)",
      padding: "12px 14px"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--success)" }} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{name}</span>
        <span style={{ fontSize: 12, color: "var(--slate)" }}>{pct}%</span>
      </div>
      <div style={{ width: "100%", height: 8, borderRadius: 6, background: "var(--bar-track)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 6, background: "var(--bar-fill)" }} />
      </div>
    </div>
  );
}

function ProviderLogo({ text }) {
  return (
    <span style={{ fontSize: 15, fontWeight: 600, color: "var(--charcoal)", fontFamily: "var(--font-display)" }}>
      {text}
    </span>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div style={{
      background: "var(--card-surface)",
      border: "1px solid var(--stone)",
      borderRadius: "var(--radius-lg)",
      padding: 28
    }}>
      <div style={{ marginBottom: 14 }}>{icon}</div>
      <h3 style={{ fontSize: 16, marginBottom: 8 }}>{title}</h3>
      <p style={{ fontSize: 14, color: "var(--slate)", lineHeight: 1.6 }}>{desc}</p>
    </div>
  );
}
